import { theme } from '@arnold/common';
import styled from '@emotion/styled';
import type { SummarySentiment } from './summaryData';

const sentimentStyle = {
  negative: {
    dot: theme.colors.emotionDanger.default,
    text: theme.colors.emotionDanger.hover,
    background: theme.colors.emotionDanger.background,
  },
  positive: {
    dot: theme.colors.emotionSuccess.default,
    text: theme.colors.emotionSuccess.hover,
    background: theme.colors.emotionSuccess.background,
  },
  neutral: {
    dot: theme.colors.iconText.secondary,
    text: theme.colors.text.primary,
    background: theme.colors.emotionNeutral.background,
  },
};

export const Card = styled.section`
  overflow: hidden;
  border-radius: ${theme.borderRadius.xl};
  background: ${theme.colors.backgroundCover.default};
  box-shadow: ${theme.shadows.elevationContentBlockHigh};
  container-type: inline-size;
  container-name: ai-summary;
`;
export const Header = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 24px;
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
  @container ai-summary (max-width: 500px) {
    padding: 16px;
  }
`;
export const AiBadge = styled.span`
  flex-shrink: 0;
  padding: 2px 5px;
  border-radius: 4px;
  background: #f1e6ff;
  color: ${theme.colors.ai.default};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;
export const HeaderBody = styled.div`
  flex: 1;
  min-width: 0;
  > p {
    margin: 6px 0 0;
    font-size: 14px;
    color: ${theme.colors.text.secondary};
  }
`;
export const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    line-height: 30px;
  }
`;
export const CountBadge = styled.span<{ sentiment: SummarySentiment }>`
  padding: 6px 8px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 16px;
  background: ${({ sentiment }) => sentimentStyle[sentiment].background};
  color: ${({ sentiment }) => sentimentStyle[sentiment].text};
`;
export const CircleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid ${theme.colors.actionPrimary.default};
  border-radius: 50%;
  background: transparent;
  color: ${theme.colors.actionPrimary.default};
  cursor: pointer;
  > svg {
    width: 20px;
    height: 20px;
    path {
      fill: currentColor;
    }
  }
  &:hover:not(:disabled) {
    background: ${theme.colors.emotionInformative.background};
  }
  &:focus-visible {
    outline: 2px solid ${theme.colors.actionPrimary.focused};
    outline-offset: 3px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;
export const RefreshButton = styled(CircleButton)`
  border-color: ${theme.colors.borderMain.default};
  color: ${theme.colors.iconText.primary};
`;
export const Body = styled.div`
  padding: 24px;
  @container ai-summary (max-width: 500px) {
    padding: 16px;
  }
`;
export const Preview = styled.div`
  max-height: 200px;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
  mask-image: linear-gradient(to bottom, #000 15%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, #000 15%, transparent 100%);
`;
export const Group = styled.section`
  + section {
    margin-top: 32px;
  }
  h3 {
    margin: 0 0 12px;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
  }
  > ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
`;
export const Point = styled.li`
  padding: 16px 0;
  + li {
    border-top: 1px solid ${theme.colors.borderSeparator.default};
  }
`;
export const PointRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  > p {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 16px;
    line-height: 24px;
  }
`;
export const SentimentDot = styled.span<{ sentiment: SummarySentiment }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 8px;
  border-radius: 50%;
  background: ${({ sentiment }) => sentimentStyle[sentiment].dot};
`;
export const SourceQuestions = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 24px;
  margin-left: 24px;
  padding-top: 24px;
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  container-type: inline-size;
`;
export const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 24px;
`;
export const ToggleButton = styled.button`
  padding: 10px 24px;
  border: 1px solid ${theme.colors.actionPrimary.default};
  border-radius: 24px;
  background: transparent;
  color: ${theme.colors.actionPrimary.default};
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  &:hover {
    background: ${theme.colors.emotionInformative.background};
  }
  &:focus-visible {
    outline: 2px solid ${theme.colors.actionPrimary.focused};
    outline-offset: 3px;
  }
`;
